import { Base64 } from 'js-base64';
import { ENABLE_GIFT_CARD_TOKENIZATION } from './ld-user';

const setupThreeDSComponent = async (royalPaywayElement) => {
  const threeDsComponentElement = royalPaywayElement.current;
  // call setup to initiate the flow. If the paywayThreeDsParam exists in the URL,
  // that means the challenge flow has been completed and we're supposed to retrieve the
  // transaction instead of creating a new session.
  await threeDsComponentElement.setup();
};

export const setupPaywayInterval = (royalPayway) => {
  const paywayInterval = setInterval(() => {
    if (royalPayway.current && royalPayway.current.setup) {
      // check if threeds script is ready
      clearInterval(paywayInterval);
      setupThreeDSComponent(royalPayway);
    }
  }, 500);
};

export const runGatewayPaywayValidation = ({
  cardNumber,
  firstNameOnCard,
  lastNameOnCard,
  amount,
  variables,
  royalPayway,
  setShowLoader,
  country,
  month,
  year,
  currentBooking,
  giftCards,
  handlePaymentApiAndThreeDs,
  showWarningNotification,
  showErrorNotification,
  setMakePaymentInfo,
}) => {
  const threeDsComponentElement = royalPayway.current;
  // trigger initiateAndAuthenticate on form submit.
  setShowLoader(true);
  threeDsComponentElement
    .initiateAndAuthenticate({
      // correlationId: heldConfirmInput.correlationID,
      order: {
        amount,
        country,
        currency: String(currentBooking.price.currencyCode),
        bookingId: String(+currentBooking.id),
        // Modal validation works whit this currencies: USD, NDK, DKK, GBP, EUR
        // referenceId: 'SHOREX-12345', // optional
      },
      paymentMethod: {
        cardNumber: String(cardNumber),
        cardholder: `${firstNameOnCard} ${lastNameOnCard}`,
        // or cardToken: '2223002562670007',
        expirationMonth: month,
        expirationYear: year,
      },
    })
    .then((threeDsSession) => {
      switch (threeDsSession.status) {
        case 'SKIPPED':
        case 'AUTH_COMPLETED':
          handlePaymentApiAndThreeDs(
            threeDsComponentElement,
            variables,
            threeDsSession,
            setShowLoader,
            setMakePaymentInfo,
            giftCards,
            currentBooking,
            country,
            showErrorNotification,
          );
          break;
        case 'CANCELLED':
          setShowLoader(false);
          showWarningNotification();
          break;
        case 'DECLINED':
        case 'ERROR':
          setShowLoader(false);
          showErrorNotification();
          break;
        default:
          break;
      }
    })
    .catch(() => {
      showErrorNotification();
      setShowLoader(false);
    });
};

export const tokenizeGiftCard = (
  threeDsComponentElement,
  giftCardsFor3ds,
  paymentInfo,
  setMakePaymentInfo,
  setShowLoader,
  showErrorNotification,
  creditCardInfo,
) => {
  // trigger tokenizeCards on form submit.
  threeDsComponentElement
    .tokenizeCards(giftCardsFor3ds)
    .then(async (threeDsSessionGiftCards) => {
      const haveErrors = threeDsSessionGiftCards.find((card) => card.errorDescription);
      if (!haveErrors) {
        const cardsTokens = creditCardInfo ? [creditCardInfo] : [];
        threeDsSessionGiftCards.forEach((giftcard) => {
          const { expirationMonth } = giftcard.paymentMethod;
          const { expirationYear } = giftcard.paymentMethod;
          const cardFound = paymentInfo.find((card) =>
            card.cardNumber.endsWith(giftcard.correlationId),
          );
          cardsTokens.push({
            cardType: 'VA',
            cardNumber: giftcard.paymentMethod.cardToken,
            expiration: `${expirationMonth}${expirationYear.slice(-2)}`,
            amount: cardFound.amount,
            cvv: cardFound.cvv,
            isThreeDsEligible: false,
            paymentMethod: 'GC',
          });
        });
        const payloadMakePayment = {
          variables: {
            makePaymentInput: cardsTokens,
          },
        };

        setShowLoader(true);
        await setMakePaymentInfo(payloadMakePayment);
      } else {
        setShowLoader(false);
        showErrorNotification();
      }
    })
    .catch(() => {
      setShowLoader(false);
      showErrorNotification();
    });
};

export const handlePaymentApiAndThreeDs = async (
  threeDsComponentElement,
  variables,
  threeDsSession,
  setShowLoader,
  setMakePaymentInfo,
  giftCards,
  currentBooking,
  country,
  showErrorNotification,
  ldFlags,
) => {
  const authenticationVar =
    threeDsSession && threeDsSession.authentication
      ? JSON.stringify(threeDsSession.authentication)
      : '';
  const paymentInfo = [];
  let creditCardInfo;

  if (variables) {
    const { cardType, cardNumber, expiration, firstNameOnCard, lastNameOnCard, amount, cvv } =
      variables.variables;

    creditCardInfo = {
      cardType,
      cardNumber,
      expiration,
      firstNameOnCard,
      lastNameOnCard,
      isThreeDsEligible:
        threeDsSession && threeDsSession.isThreeDsEligible
          ? threeDsSession.isThreeDsEligible
          : false,
      authentication: threeDsSession ? Base64.encode(authenticationVar) : '',
      amount,
      cvv,
      paymentMethod: 'CC',
    };

    paymentInfo.push(creditCardInfo);
  }

  if (giftCards && giftCards.length > 0) {
    const giftCardsFor3ds = [];
    const ldFlags3DsTokenizeGiftCard = ldFlags?.[ENABLE_GIFT_CARD_TOKENIZATION];
    giftCards.forEach((giftcard) => {
      const giftCardNumber = giftcard.info.cardNumber.replace(/\s/g, '');
      paymentInfo.push({
        cardType: 'VA',
        cardNumber: giftCardNumber,
        expiration:
          String(giftcard.info.expiryMonth).padStart(2, '0') + giftcard.info.expiryYear.slice(-2),
        amount: giftcard.chargedNotFormatted,
        cvv: giftcard.info.securityCode,
        paymentMethod: 'GC',
      });

      giftCardsFor3ds.push({
        correlationId: giftCardNumber.substr(-4),
        order: {
          bookingId: String(+currentBooking.id),
          currency: String(currentBooking.price.currencyCode),
          country,
        },
        paymentMethod: {
          cardNumber: giftCardNumber,
          expirationYear: giftcard.info.expiryYear,
          expirationMonth: String(giftcard.info.expiryMonth).padStart(2, '0'),
        },
      });
    });
    if (ldFlags3DsTokenizeGiftCard) {
      tokenizeGiftCard(
        threeDsComponentElement,
        giftCardsFor3ds,
        paymentInfo,
        setMakePaymentInfo,
        setShowLoader,
        showErrorNotification,
        creditCardInfo,
      );
    } else {
      const payloadMakePayment = {
        variables: {
          makePaymentInput: paymentInfo,
        },
      };

      setShowLoader(true);
      await setMakePaymentInfo(payloadMakePayment);
    }
  } else {
    const payloadMakePayment = {
      variables: {
        makePaymentInput: paymentInfo,
      },
    };

    setShowLoader(true);

    await setMakePaymentInfo(payloadMakePayment);
  }
};

export const displayWarningNotification = (setNotification, renderContent) => {
  setNotification({
    title: renderContent(
      'makePaymentAmendCancelPaywayNotificationTitle',
      'Unable to complete booking',
    ),
    description: renderContent(
      'makePaymentAmendCancelPaywayNotificationDescription',
      'Please complete authentication in order to process payment and complete your booking.',
    ),
    type: 'warning',
    open: true,
  });
};

export const displayErrorNotification = (setNotification, renderContent) => {
  setNotification({
    title: renderContent('makePaymentAmendErrorPaywayNotificationTitle', 'Payment error'),
    description: renderContent(
      'makePaymentAmendErrorPaywayNotificationDescription',
      'Sorry, something went wrong, we are unable to complete your transaction…',
    ),
    type: 'error',
    open: true,
  });
};

export const displayErrorNotificationAffirm = (setNotification, renderContent) => {
  setNotification({
    title: renderContent(
      'makePaymentAmendErrorPaywayNotificationAffirmTitle',
      'There was an error with your payment',
    ),
    description: renderContent(
      'makePaymentAmendErrorPaywayNotificationAffirmDescription',
      "Unfortunately you're not eligible to checkout with Affirm. Please use another payment method.",
    ),
    type: 'error',
    open: true,
  });
};
